// ============================================================
// PhotoUploader — drag & drop photo upload component
// Features:
//   - Drag & drop or click to select
//   - Upload progress per file
//   - Live preview thumbnails
//   - Delete individual photos
//   - First photo = main photo (marked with badge)
// ============================================================

import React, { useState, useRef } from 'react';
import { uploadPhotos, deletePhoto } from '../api/client';
import './PhotoUploader.css';

/**
 * PhotoUploader component
 *
 * Props:
 *   photos: string[]        — current photo URLs (controlled)
 *   onChange: (urls) => {}  — called when photos list changes
 */
export default function PhotoUploader({ photos = [], onChange }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // "Загружаем 3 фото..."
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef();

  // Resolve a URL for display — /uploads/ paths work as relative URLs via Nginx
  function resolveUrl(url) {
    return url;
  }

  async function handleFiles(files) {
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;

    setError(null);
    setUploading(true);
    setUploadProgress(`Загружаем ${imageFiles.length} фото...`);

    try {
      const newUrls = await uploadPhotos(imageFiles);
      onChange([...photos, ...newUrls]);
      setUploadProgress(null);
    } catch (err) {
      console.error('[PhotoUploader] Upload error:', err?.response?.status, err?.response?.data, err?.message);
      const msg = err?.response?.data?.error || err?.message || 'Неизвестная ошибка';
      setError(`Ошибка загрузки: ${msg}`);
      setUploadProgress(null);
    } finally {
      setUploading(false);
      // Reset file input so the same file can be re-selected
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  }

  function handleRemove(urlToRemove) {
    // If it's a server-uploaded file, delete from server too
    if (urlToRemove.startsWith('/uploads/')) {
      const filename = urlToRemove.split('/uploads/')[1];
      deletePhoto(filename).catch(() => {}); // non-blocking, best-effort
    }
    onChange(photos.filter(u => u !== urlToRemove));
  }

  function moveLeft(index) {
    if (index === 0) return;
    const updated = [...photos];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    onChange(updated);
  }

  function moveRight(index) {
    if (index === photos.length - 1) return;
    const updated = [...photos];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    onChange(updated);
  }

  return (
    <div className="photo-uploader">

      {/* Drop zone */}
      <div
        className={`drop-zone ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
        onClick={() => !uploading && inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={e => handleFiles(e.target.files)}
        />

        {uploading ? (
          <>
            <div className="drop-spinner" />
            <p>{uploadProgress}</p>
          </>
        ) : (
          <>
            <div className="drop-icon">📷</div>
            <p className="drop-title">Перетащите фото сюда или нажмите для выбора</p>
            <p className="drop-hint">JPG, PNG, WEBP до 20 МБ · несколько файлов сразу</p>
          </>
        )}
      </div>

      {error && <div className="alert alert-error" style={{ marginTop: 8 }}>{error}</div>}

      {/* Photo grid */}
      {photos.length > 0 && (
        <div className="photo-grid">
          {photos.map((url, i) => (
            <div key={url} className="photo-thumb">
              <img
                src={resolveUrl(url)}
                alt={`Фото ${i + 1}`}
                onError={e => { e.target.src = 'https://via.placeholder.com/120x80?text=Ошибка'; }}
              />

              {/* Main photo badge */}
              {i === 0 && (
                <span className="photo-main-badge">ГЛАВНАЯ</span>
              )}

              {/* Controls overlay */}
              <div className="photo-controls">
                <button
                  title="Переместить влево"
                  onClick={() => moveLeft(i)}
                  disabled={i === 0}
                  className="photo-btn"
                >←</button>
                <button
                  title="Удалить"
                  onClick={() => handleRemove(url)}
                  className="photo-btn photo-btn-delete"
                >✕</button>
                <button
                  title="Переместить вправо"
                  onClick={() => moveRight(i)}
                  disabled={i === photos.length - 1}
                  className="photo-btn"
                >→</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {photos.length > 0 && (
        <p className="photo-hint-text">
          ← → для изменения порядка · Первое фото — главное в карточке · {photos.length} фото
        </p>
      )}
    </div>
  );
}
